/** Sticky clickable TOC for downloaded Legend Sovereign case-file HTML. */

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function decodeEntities(text) {
  return String(text || '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"');
}

function plainHeading(html) {
  return decodeEntities(String(html || '').replace(/<[^>]+>/g, '')).trim();
}

export function sectionAnchorId(heading, used = {}) {
  const slug =
    String(heading || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'section';
  const base = `toc-${slug}`;
  const count = used[base] || 0;
  used[base] = count + 1;
  return count === 0 ? base : `${base}-${count + 1}`;
}

const STICKY_TOC_CSS = `
    html { scroll-behavior: smooth; }
    body { padding: 0; }
    .report-header {
      position: sticky;
      top: 0;
      z-index: 30;
      background: #0a0a0a;
      border-bottom: 1px solid rgba(45, 212, 191, 0.28);
      padding: 0.85rem 0 0.75rem;
    }
    .report-header .wrap { padding: 0 2rem; }
    .report-header h1 {
      color: #5eead4;
      font-size: 1.35rem;
      margin: 0;
    }
    .report-toc {
      display: flex;
      flex-wrap: wrap;
      gap: 0.4rem 0.55rem;
      margin-top: 0.65rem;
    }
    .report-toc a {
      color: #99f6e4;
      text-decoration: none;
      font-size: 0.78rem;
      line-height: 1.25;
      border: 1px solid rgba(45, 212, 191, 0.32);
      border-radius: 4px;
      padding: 0.22rem 0.55rem;
      background: rgba(45, 212, 191, 0.06);
    }
    .report-toc a:hover,
    .report-toc a:focus {
      color: #ccfbf1;
      background: rgba(45, 212, 191, 0.16);
    }
    .report-body { padding: 0.5rem 2rem 2.5rem; }
    a.toc-target {
      display: block;
      height: 0;
      scroll-margin-top: 7.5rem;
    }
    @media print {
      .report-header { position: static; }
      html { scroll-behavior: auto; }
    }
`;

export function ensureHtmlReportToc(html, title = '') {
  let updated = String(html || '');
  if (!updated.trim()) return updated;
  const used = {};
  const items = [];
  updated = updated.replace(
    /(?:<a class="toc-target"[^>]*><\/a>\s*)?(<section class="report-section">\s*<h2><strong>)(.*?)(<\/strong><\/h2>)/gi,
    (_full, _prefix, headingHtml) => {
      const heading = plainHeading(headingHtml);
      const anchor = sectionAnchorId(heading, used);
      if (heading) items.push([heading, anchor]);
      return `<a class="toc-target" id="${escapeHtml(anchor)}"></a>\n<section class="report-section">\n  <h2><strong>${headingHtml}</strong></h2>`;
    }
  );
  if (!items.length) return updated;
  const toc = `<nav class="report-toc" aria-label="Case file sections">${items
    .map(([heading, anchor]) => `<a href="#${escapeHtml(anchor)}">${escapeHtml(heading)}</a>`)
    .join('')}</nav>`;
  const titleMatch = updated.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i);
  const titleText = String(title || '').trim() || plainHeading(titleMatch?.[1] || '') || 'Tier 1 Analysis Report';
  const header = `<header class="report-header">
    <div class="wrap">
      <h1>${escapeHtml(titleText)}</h1>
      ${toc}
    </div>
  </header>
`;
  const cssMissing = !updated.includes('position: sticky');
  if (/class=["']report-header["']/i.test(updated)) {
    updated = updated.replace(/<nav class="report-toc"[^>]*>[\s\S]*?<\/nav>/i, toc);
  } else if (/<body[^>]*>/i.test(updated)) {
    updated = updated.replace(/(<div class="wrap">\s*)<h1[^>]*>[\s\S]*?<\/h1>/i, '$1');
    updated = updated.replace(/<div class="wrap">/i, '<div class="wrap report-body">');
    updated = updated.replace(/<body[^>]*>/i, (open) => `${open}\n  ${header}`);
  }
  if (cssMissing) {
    if (updated.includes('</style>')) {
      updated = updated.replace('</style>', `${STICKY_TOC_CSS}\n  </style>`);
    } else {
      updated = updated.replace(/<\/head>/i, `<style>${STICKY_TOC_CSS}</style>\n</head>`);
    }
  }
  return updated;
}

const CASE_FILE_STICKY_TOC_CSS = `
    html { scroll-behavior: smooth; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      margin: 0;
      padding: 0;
      background: #f9fafb;
      color: #111827;
    }
    .report-header {
      position: sticky;
      top: 0;
      z-index: 30;
      background: #f9fafb;
      border-bottom: 1px solid #d1d5db;
      padding: 16px 20px 12px;
    }
    .report-header-inner { max-width: 1200px; margin: 0 auto; }
    .report-header h1 {
      font-size: 28px;
      font-weight: 700;
      margin: 0;
      color: #0d9488;
    }
    .report-toc {
      display: flex;
      flex-wrap: wrap;
      gap: 6px 8px;
      margin-top: 10px;
    }
    .report-toc a {
      color: #0f766e;
      text-decoration: none;
      font-size: 12px;
      line-height: 1.25;
      border: 1px solid #99f6e4;
      border-radius: 4px;
      padding: 3px 8px;
      background: #f0fdfa;
    }
    .report-toc a:hover,
    .report-toc a:focus {
      background: #ccfbf1;
    }
    .meta {
      color: #6b7280;
      font-size: 14px;
      margin-top: 8px;
    }
    .report-body {
      max-width: 1200px;
      margin: 0 auto;
      padding: 16px 20px 40px;
    }
    a.toc-target {
      display: block;
      height: 0;
      scroll-margin-top: 120px;
    }
    .section {
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 24px;
      margin-bottom: 24px;
      page-break-inside: avoid;
    }
    .section-title {
      font-size: 20px;
      font-weight: 600;
      margin: 0 0 16px;
      color: #0d9488;
      border-bottom: 2px solid #14b8a6;
      padding-bottom: 8px;
    }
    .target-card, .field-card {
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 16px;
      margin: 12px 0;
      background: #f9fafb;
      page-break-inside: avoid;
    }
    .target-card h3, .field-card h3 {
      margin: 0 0 8px;
      color: #0f766e;
      font-size: 16px;
    }
    .field-label {
      font-weight: 600;
      color: #374151;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin: 8px 0 4px;
    }
    .field-value {
      color: #1f2937;
      white-space: pre-wrap;
      font-size: 14px;
      line-height: 1.5;
    }
    @media print {
      .report-header { position: static; }
      .report-toc { display: none; }
      html { scroll-behavior: auto; }
      body { margin: 0; padding: 20px; }
      .section { page-break-inside: avoid; }
    }
`;

function fieldCard(heading, content) {
  if (!content) return '';
  return `      <div class="field-card">
        <h3>${escapeHtml(heading)}</h3>
        <div class="field-value">${escapeHtml(content)}</div>
      </div>
`;
}

export function buildSovereignCaseFileHtml(parsed, title, extras = {}) {
  const sections = parsed?.sections || [];
  const investigatorNotes = extras.investigatorNotes || extras.investigator_notes || '';
  const heading = String(title || '').trim() || 'Sovereign Assessment';
  const used = {};
  const tocItems = sections.map((section) => {
    const name = section.title || 'Section';
    return { heading: name, anchor: sectionAnchorId(name, used) };
  });
  if (String(investigatorNotes).trim()) {
    tocItems.push({ heading: 'Investigator Case Notes', anchor: 'toc-investigator-case-notes' });
  }
  const toc = tocItems
    .map((item) => `<a href="#${escapeHtml(item.anchor)}">${escapeHtml(item.heading)}</a>`)
    .join('');
  let body = '';
  sections.forEach((section, idx) => {
    const item = tocItems[idx];
    body += `  <div class="section">
    <a class="toc-target" id="${escapeHtml(item.anchor)}"></a>
    <h2 class="section-title">${escapeHtml(section.title || 'Section')}</h2>
`;
    if (section.targets?.length) {
      section.targets.forEach((target) => {
        body += `    <div class="target-card">
      <h3>${escapeHtml(`${target.num || ''}. ${target.name || ''}`.trim())}</h3>
`;
        [
          ['Angle', target.angle],
          ['Evidence', target.evidence],
          ['Risk', target.risk],
          ['Role', target.role],
          ['Relationship', target.relationship],
        ].forEach(([label, value]) => {
          if (!value) return;
          body += `      <div class="field-label">${escapeHtml(label)}</div>
      <div class="field-value">${escapeHtml(value)}</div>
`;
        });
        body += `    </div>
`;
      });
    } else if (section.rows?.length) {
      section.rows.forEach((row) => {
        body += fieldCard(row.heading || '', row.content || '');
      });
    }
    body += `  </div>
`;
  });
  if (String(investigatorNotes).trim()) {
    body += `  <div class="section">
    <a class="toc-target" id="toc-investigator-case-notes"></a>
    <h2 class="section-title" style="color: #d97706; border-bottom-color: #f59e0b;">Investigator Case Notes</h2>
    <div class="field-card" style="background: #fef3c7;">
      <div class="field-value">${escapeHtml(investigatorNotes)}</div>
    </div>
  </div>
`;
  }
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(heading)}</title>
  <style>${CASE_FILE_STICKY_TOC_CSS}
  </style>
</head>
<body>
  <header class="report-header">
    <div class="report-header-inner">
      <h1>${escapeHtml(heading)}</h1>
      <nav class="report-toc" aria-label="Case file sections">${toc}</nav>
      <div class="meta">Investigator Case File - ${escapeHtml(new Date().toLocaleDateString())}</div>
    </div>
  </header>
  <div class="report-body">
${body}  </div>
</body>
</html>`;
}
